<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { DesignItem } from '@core/types';
import { t } from './i18n';
import {
  SHAPE_DEFINITIONS,
  renderShape,
  type ShapeDefinition,
  type ShapeDesignItem,
} from './shapes';
import { fetchPublicSettings, type CustomLibraryItem } from './settingsApi';

type DesignerCommands = {
  addItem?: (item: ShapeDesignItem | DesignItem, options?: { placement?: 'print-area-center' }) => void;
  selectItem?: (id: string) => void;
};

const hiddenShapeIds = ref<Set<string>>(new Set());
const customShapes = ref<CustomLibraryItem[]>([]);

const visibleShapes = computed(() => SHAPE_DEFINITIONS.filter((shape) => !hiddenShapeIds.value.has(shape.id)));

function nextId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `shape-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function commands(): DesignerCommands | null {
  return (window as unknown as { Veyop?: { designer?: { commands?: DesignerCommands } } }).Veyop?.designer?.commands ?? null;
}

function addShape(shape: ShapeDefinition) {
  const shapeParams = { fill: '#111111', stroke: '#000000', strokeWidth: 2, cornerRadius: 14 };
  const rendered = renderShape(shape.id, shapeParams, {}, shape.defaultWidth, shape.defaultHeight);
  const item: ShapeDesignItem = {
    id: nextId(),
    type: 'svg',
    top: 50,
    left: 50,
    width: shape.defaultWidth,
    height: shape.defaultHeight,
    angle: 0,
    zIndex: 0,
    shapeId: shape.id,
    shapeParams,
    shapeSourceSvg: rendered.source,
    svg: rendered.svg,
    src: rendered.src,
    change_color: true,
    svgColors: {},
  };
  const cmd = commands();
  cmd?.addItem?.(item, { placement: 'print-area-center' });
}

function addCustomShape(shape: CustomLibraryItem) {
  const item: DesignItem = {
    id: nextId(),
    type: 'svg',
    top: 50,
    left: 50,
    width: 120,
    height: 120,
    angle: 0,
    zIndex: 0,
    src: shape.url,
    fileUrl: shape.url,
    change_color: true,
    svgColors: {},
  };
  const cmd = commands();
  cmd?.addItem?.(item, { placement: 'print-area-center' });
}

onMounted(async () => {
  const settings = await fetchPublicSettings();
  hiddenShapeIds.value = new Set(settings.hidden_shape_ids);
  customShapes.value = settings.customShapes;
});
</script>

<template>
  <section class="vptw:flex vptw:flex-col vptw:gap-3" :aria-label="t('Shapes')">
    <div class="vptw:grid vptw:grid-cols-3 vptw:gap-2">
      <button
        v-for="shape in visibleShapes"
        :key="shape.id"
        type="button"
        class="vptw:flex! vptw:h-20 vptw:flex-col vptw:items-center vptw:justify-center vptw:gap-1 vptw:rounded-lg! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-2! vptw:text-(--vs-ink-2)! vptw:transition vptw:hover:border-(--vs-accent)! vptw:hover:bg-(--vs-accent-soft)! vptw:cursor-pointer"
        :title="t(shape.label)"
        :aria-label="`${t('Insert')} ${t(shape.label)}`"
        @click="addShape(shape)"
      >
        <svg viewBox="0 0 24 24" class="vptw:size-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">
          <path :d="shape.icon" />
        </svg>
        <span class="vptw:max-w-full vptw:truncate vptw:text-[10px] vptw:font-medium">{{ t(shape.label) }}</span>
      </button>
    </div>

    <div v-if="customShapes.length" class="vptw:grid vptw:grid-cols-3 vptw:gap-2 vptw:border-t vptw:border-(--vs-line) vptw:pt-3">
      <button
        v-for="shape in customShapes"
        :key="shape.id"
        type="button"
        class="vptw:flex! vptw:h-20 vptw:flex-col vptw:items-center vptw:justify-center vptw:gap-1 vptw:rounded-lg! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-2! vptw:text-(--vs-ink-2)! vptw:transition vptw:hover:border-(--vs-accent)! vptw:hover:bg-(--vs-accent-soft)! vptw:cursor-pointer"
        :title="shape.label"
        :aria-label="`${t('Insert')} ${shape.label}`"
        @click="addCustomShape(shape)"
      >
        <img :src="shape.url" :alt="shape.label" class="vptw:size-6 vptw:object-contain" loading="lazy" />
        <span class="vptw:max-w-full vptw:truncate vptw:text-[10px] vptw:font-medium">{{ shape.label }}</span>
      </button>
    </div>
  </section>
</template>
